import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { isProjectRoom } from "shared/types/Project"
import { Room } from "shared/types/Room"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { RoomTitle } from "entities/rooms/ui/room-title/RoomTitle"

import "./styles/_room-input.scss"

interface ParentOptionValueProps {
  value: SelectOption<string, Room> | null
}

const ParentOptionValue = React.memo(({ value }: ParentOptionValueProps) => {
  const { t } = useTranslation()

  if (!value?.data) {
    return value ? (
      <span>{value.label}</span>
    ) : (
      <span className="combobox__placeholder">{t("General.Select")}</span>
    )
  }

  if (isProjectRoom(value.data)) {
    return (
      <RoomTitle
        room={{
          label: value.data.label,
          number: value.data.number
        }}
      />
    )
  }

  return <span>{value.label}</span>
})

ParentOptionValue.displayName = "ParentOptionValue"

export interface RoomInputProps extends WithClassName {
  options: SelectOption<string, Room>[] | undefined
  name?: string
  readonly?: boolean
  loading?: boolean
  disabled?: boolean
}

export const RoomInput = React.memo(
  ({
    options,
    readonly,
    loading,
    disabled,
    name,
    className
  }: RoomInputProps) => {
    const { t } = useTranslation()

    const renderValue = useCallback(
      (value: SelectOption<string, Room> | null) => (
        <ParentOptionValue value={value} />
      ),
      []
    )

    return (
      <Combobox.Root
        name={name}
        inputID="room-input"
        data-testid="room-input"
        label={t("Room.Room")}
        className={classNames("room-input", className)}
        readonly={readonly}
        loading={loading}
        disabled={disabled}
        items={options || []}
      >
        <Combobox.Label />
        {!readonly && (
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>{renderValue}</Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner
                className="room-input__positioner"
                align="start"
                sideOffset={8}
              >
                <Combobox.Popup aria-label={t("Room.Room")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<string, Room>) => (
                      <Combobox.Item key={option.value} value={option}>
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          <ParentOptionValue value={option} />
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
        )}
        <Combobox.Readonly items={options || []} />
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
